import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type AssetListResponse,
  type PackingWriteResponse,
  type PublicAsset,
  type PublicTote,
  type ToteListResponse,
  type TrailerResponse,
} from '@inventohub/shared';
import { HelpLink } from '../components/HelpLink';
import { PackingEditor } from '../components/PackingEditor';
import { ApiClientError, apiRequest } from '../lib/api';

export function TrailerDetailPage(): JSX.Element {
  const { id } = useParams();
  const [detail, setDetail] = useState<TrailerResponse | null>(null);
  const [totes, setTotes] = useState<PublicTote[]>([]);
  const [assets, setAssets] = useState<PublicAsset[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const [trailerBody, toteBody, assetBody] = await Promise.all([
          apiRequest<TrailerResponse>(`/api/trailers/${id}`),
          apiRequest<ToteListResponse>('/api/totes'),
          apiRequest<AssetListResponse>('/api/assets'),
        ]);
        if (cancelled) return;
        setDetail(trailerBody);
        setTotes(toteBody.totes);
        setAssets(assetBody.assets);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load this trailer.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  function onPacked(body: PackingWriteResponse): void {
    setDetail((current) =>
      current
        ? { ...current, packedTotes: body.packedTotes, packedAssets: body.packedAssets }
        : current,
    );
  }

  if (!detail && !error) {
    return (
      <section className="card">
        <p>Loading trailer…</p>
      </section>
    );
  }

  return (
    <section data-testid="trailer-detail-page">
      <div className="card">
        <p className="meta">
          <Link to="/trailers">Trailers and shipments</Link>
        </p>
        <div className="card__title-row">
          <h2>{detail?.trailer.name ?? 'Trailer'}</h2>
          <HelpLink slug="shipped-via" />
        </div>
        <p className="meta">EWP trailer. Packing is how kits ride.</p>
        {error ? <p className="status status--error">{error}</p> : null}
        {detail?.trailer.notes ? <p>{detail.trailer.notes}</p> : null}
      </div>
      {detail ? (
        <PackingEditor
          packingPath={`/api/trailers/${detail.trailer.id}/packing`}
          packedTotes={detail.packedTotes}
          packedAssets={detail.packedAssets}
          totes={totes}
          assets={assets}
          onPacked={onPacked}
        />
      ) : null}
    </section>
  );
}
